import React, { useState, useMemo, useRef, useContext } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { WebAPIContext } from "../../contexts/WebAPIContext";
import {
  BenchmarkItem,
  PartNoDetail,
  PastProjectSearchItem,
  ProjectBenchmarkResponse,
  SelectedPastBenchmark,
} from "../../contexts/types";
import { PurposeType } from "../../contexts/constants";
import FilterTextBox from '../../components/FilterTextBox';
import TruncatedCell from '../../components/TruncatedCell';
import { resolveDisplayPartNoByKey } from '../../utils/utils';

interface PastBenchmarkSearchPanelProps {
  /** パネルを閉じるためのハンドラー */
  onClose: () => void;
}
const BP = "features.InvestmentScreening.BenchmarkInputPopover";

const PastBenchmarkSearchPanel: React.FC<PastBenchmarkSearchPanelProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const WebAPI = useContext(WebAPIContext);
  const TITLE = t(`${BP}.title`);
  const toast = useToast();

  /** Draggable 用 nodeRef（React StrictMode 対応） */
  const draggableRef = useRef<HTMLDivElement>(null);

  /** フローティングパネルの初期位置 */
  const panelDefaultPos = useRef({ x: Math.max(20, (window.innerWidth - 860) / 2), y: 80 });

  /** Step管理: "project" | "item" | "result" */
  const [searchStep, setSearchStep] = useState<"project" | "item" | "result">("project");

  /** PartNo検索入力値 */
  const [searchPartNoInput, setSearchPartNoInput] = useState("");

  /** PJ検索結果（searchIncreasedProductionByPartNo の戻り値） */
  const [appResults, setAppResults] = useState<PastProjectSearchItem[]>([]);
  const [reviewResults, setReviewResults] = useState<PastProjectSearchItem[]>([]);

  /** 検索結果の件数超過フラグ */
  const [appHasMore, setAppHasMore] = useState(false);
  const [reviewHasMore, setReviewHasMore] = useState(false);

  /** ステップ2で閲覧中のprojectId */
  const [browsingProjectId, setBrowsingProjectId] = useState<string | null>(null);

  /** ステップ2で閲覧中のprojectName（ヘッダー表示用） */
  const [browsingProjectName, setBrowsingProjectName] = useState("");

  /** getBenchmarkByProjectId の取得済みデータ（ステップ2）*/
  const [browsingBenchmarkData, setBrowsingBenchmarkData] = useState<ProjectBenchmarkResponse | null>(null);

  /** ローディング状態 */
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  /** 選択済みの過去ベンチマーク（Step3表示用） */
  const [selectedPastBenchmark, setSelectedPastBenchmark] = useState<SelectedPastBenchmark | null>(null);

  /** 比較表示中の選択PartNo（PartNoモード設備の場合のみ使用） */
  const [comparePartNo, setComparePartNo] = useState<string | null>(null);

  //タブ用
  const [tabIndex, setTabIndex] = useState(0);

  /**カラムフィルター */
  const [filterProjectName, setFilterProjectName] = useState("");
  const [filterPartNo, setFilterPartNo] = useState("");


  /**
   * Step1: PartNo検索
   */
  const handleSearchPartNo = async () => {
    if (searchPartNoInput.trim().length < 3) {
      toast({
        title: TITLE,
        description: t(`${BP}.modal.searchPlaceholder`),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSearchLoading(true);
    try {
      const query = `
        query searchIncreasedProductionByPartNo($partNoPrefix: String, $purpose: String, $isReview: Boolean) {
          searchIncreasedProductionByPartNo(partNoPrefix: $partNoPrefix, purpose: $purpose, isReview: $isReview) {
            projects {
              projectId
              reviewDate
              projectName
              partNo
              partNoDetails {
                partNo
                displayPartNo
              }
              approveDay
              approvalStatus
            }
            hasMore
          }
        }
      `;

      const [appResponse, reviewResponse] = await Promise.all([
        // 1発目：申請時データ (isReview: false)
        WebAPI.request({
          query,
          variables: {
            partNoPrefix: searchPartNoInput,
            purpose: PurposeType.IncreasedProduction.code,
            isReview: false,
          },
        }),
        // 2発目：振り返りデータ (isReview: true)
        WebAPI.request({
          query,
          variables: {
            partNoPrefix: searchPartNoInput,
            purpose: PurposeType.IncreasedProduction.code,
            isReview: true,
          },
        })
      ]);
      //APIのレスポンス構造が正しいかチェック（両方とも想定外の構造ならエラーを投げる）
      if (!appResponse?.data?.searchIncreasedProductionByPartNo || !reviewResponse?.data?.searchIncreasedProductionByPartNo) {
        throw new Error("API call failed or returned invalid structure");
      }
      /**
       * 現在のPJを省いたデータ+規定件数を超過していないかのチェック
       * @param response 取得した検索データ
       * @returns project,hasMore
       */
      const formatResponseData = (response: any) => {
        if (!response?.data?.searchIncreasedProductionByPartNo) {
          return { projects: [], hasMore: false };
        }

        const result = response.data.searchIncreasedProductionByPartNo;
        const hasMore = result.hasMore || false;
        const currentProjectId = sessionStorage.getItem("projectId");

        // 現在のPJを省く
        const formattedProjects = (result.projects || [])
          .filter((p: any) => {
            return !(currentProjectId && p.projectId === currentProjectId)
          })
          .map((p: any) => ({
            projectId: p.projectId,
            reviewDate: p.reviewDate || "",
            projectName: p.projectName,
            partNo: p.partNo,
            partNoDetails: p.partNoDetails || [],
            approveDay: p.approveDay,
            approvalStatus: p.approvalStatus,
          }));
        return { projects: formattedProjects, hasMore };
      };

      // 整形データの格納+規定件数の判定
      const appData = formatResponseData(appResponse);
      setAppResults(appData.projects);
      setAppHasMore(appData.hasMore);

      const reviewData = formatResponseData(reviewResponse);
      setReviewResults(reviewData.projects);
      setReviewHasMore(reviewData.hasMore);

      //「0件通知」の判定（両方ともデータがなく、かつ続きのデータもない場合）
      const totalProjectsCount = appData.projects.length + reviewData.projects.length;
      const isAnyHasMore = appData.hasMore || reviewData.hasMore;
      console.log("totalProjectsCount", totalProjectsCount)
      console.log("isAnyHasMore", isAnyHasMore)
      if (totalProjectsCount === 0 && !isAnyHasMore) {
        toast({
          title: TITLE,
          description: t(`${BP}.modal.noResults`),
          status: "info",
          duration: 3000,
          isClosable: true,
        });

      }
    } catch (error) {
      console.error("Failed to search projects:", error);
      toast({
        title: TITLE,
        description: t(`${BP}.modal.noResults`),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearchLoading(false);
    }
  };

  /**
   * Step2 ヘルパー: benchmarkData JSON文字列をパースして BenchmarkItem[] に変換する
   */
  const parseBenchmarkItems = (raw: string | null | undefined, projectId: string): BenchmarkItem[] => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      console.error("Failed to parse benchmarkData JSON for projectId:", projectId);
      return [];
    }
  };

  /**
   * Step2: プロジェクト選択後、ベンチマークデータ取得
   */
  const handleSelectProject = async (projectId: string, projectName: string, reviewDate?: string | null) => {
    setIsSearchLoading(true);
    try {
      const query = `
        query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
          getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
            projectId
            benchmarkData
          }
        }
      `;

      const response = await WebAPI.request({ query, variables: { projectId, reviewDate: reviewDate || null } });

      if (response?.data?.getProjectAdditionalInfoById) {
        const raw = response.data.getProjectAdditionalInfoById?.benchmarkData;
        const benchmarks = parseBenchmarkItems(raw, projectId);

        setBrowsingBenchmarkData({ projectId, benchmarks });
        setBrowsingProjectId(projectId);
        setBrowsingProjectName(projectName);
        setSearchStep("item");
      } else {
        throw new Error("API call failed");
      }
    } catch (error) {
      console.error("Failed to fetch benchmark data:", error);
      toast({
        title: TITLE,
        description: t(`${BP}.modal.noResults`),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearchLoading(false);
    }
  };

  /**
   * Step3: 設備・ライン選択時の処理
   */
  const handleSelectBenchmark = (item: any) => {
    const selected: SelectedPastBenchmark = {
      projectId: browsingProjectId!,
      projectName: browsingProjectName,
      inputType: item.inputType,
      equipmentId: item.equipmentId,
      equipmentName: item.equipmentName,
      processCode: item.processCode,
      productionLineId: item.productionLineId,
      productionLineName: item.productionLineName,
      benchmarkMonth: item.benchmarkMonth,
      actualBest: item.actualBest,
      theoretical: item.theoretical,
      investmentPlan: item.investmentPlan,
      benchmarkByPartNo: item.benchmarkByPartNo,
      gapAnalysis: item.freeInputFields.gapAnalysis,
      productionPerEquipment: item.freeInputFields.productionPerEquipment,
      productionPerPerson: item.freeInputFields.productionPerPerson,
      nonOperationMeasures: item.freeInputFields.nonOperationMeasures,
      yieldMeasures: item.freeInputFields.yieldMeasures,
    };

    setSelectedPastBenchmark(selected);
    setComparePartNo(item.selectedPartNo ?? null);
    setSearchStep("result");
  };


  /**
   * PJテータのフィルター処理
   * ProjectName,PartNo
   */
  const appFilterData = useMemo(() => {
    return appResults.filter(r => {
      if (filterProjectName) {
        // 元データとフィルターワードを小文字化
        const targetProject = r.projectName.toLowerCase();
        const searchWord = filterProjectName.toLowerCase();
        // 一致しない要素を弾く
        if (!targetProject.includes(searchWord)) {
          return false;
        }
      }
      if (filterPartNo) {
        const searchWord = filterPartNo.toLowerCase();
        const targetPartNo = r.partNo.toLowerCase();
        const hasDisplayPartNoMatch = (r.partNoDetails || []).some(d =>
          d.displayPartNo && d.displayPartNo.toLowerCase().includes(searchWord)
        );
        if (!targetPartNo.includes(searchWord) && !hasDisplayPartNoMatch) {
          return false;
        }
      }

      // 弾かれなかった要素を返す
      return true;
    })
  }, [filterProjectName, filterPartNo, appResults])

  // 振り返りフィルターデータ
  const reviewFilterData = useMemo(() => {
    return reviewResults.filter(r => {
      if (filterProjectName) {
        // 元データとフィルターワードを小文字化
        const targetProject = r.projectName.toLowerCase();
        const searchWord = filterProjectName.toLowerCase();
        // 一致しない要素を弾く
        if (!targetProject.includes(searchWord)) {
          return false;
        }
      }
      if (filterPartNo) {
        const searchWord = filterPartNo.toLowerCase();
        const targetPartNo = r.partNo.toLowerCase();
        const hasDisplayPartNoMatch = (r.partNoDetails || []).some(d =>
          d.displayPartNo && d.displayPartNo.toLowerCase().includes(searchWord)
        );
        if (!targetPartNo.includes(searchWord) && !hasDisplayPartNoMatch) {
          return false;
        }
      }

      // 弾かれなかった要素を返す
      return true;
    })
  }, [filterProjectName, filterPartNo, reviewResults])

  // Step3: 選択中プロジェクトのPartNo詳細（displayPartNo解決用、No.435対応）
  const selectedProjectPartNoDetails = useMemo(() => {
    if (!browsingProjectId) return undefined;
    const found = appResults.find(p => p.projectId === browsingProjectId)
      ?? reviewResults.find(p => p.projectId === browsingProjectId);
    return found?.partNoDetails
      ?.filter((d): d is { partNo: string; displayPartNo?: string | null } => !!d.partNo)
      .map(d => ({ partNo: d.partNo, displayPartNo: d.displayPartNo ?? undefined }));
  }, [browsingProjectId, appResults, reviewResults]);

  // Step3: 選択中ベンチマークの比較値（PartNoモードはドロップダウン選択値を使用）
  const compareValues = useMemo(() => {
    if (!selectedPastBenchmark) return null;
    const partNoList = selectedPastBenchmark.benchmarkByPartNo
      ? Object.keys(selectedPastBenchmark.benchmarkByPartNo)
      : [];
    if (partNoList.length > 0 && comparePartNo) {
      return selectedPastBenchmark.benchmarkByPartNo![comparePartNo] ?? null;
    }
    return {
      actualBest: selectedPastBenchmark.actualBest,
      theoretical: selectedPastBenchmark.theoretical,
      investmentPlan: selectedPastBenchmark.investmentPlan,
    };
  }, [selectedPastBenchmark, comparePartNo]);

  // step1 プロジェクト一覧
  const ProjectListTable = ({
    data,
    isReview,
    isSearchLoading,
    filterProjectName,
    setFilterProjectName,
    filterPartNo,
    setFilterPartNo,
    onSelect
  }: any) => {
    const { t } = useTranslation();

    return (
      <Box overflowY="auto" flex="1" minH="0">
        {isSearchLoading ? (
          <Flex justify="center" pt={4}>
            <Spinner />
          </Flex>
        ) : data.length > 0 ? (
          <Table variant="simple" size="sm" layout="fixed" w="100%">
            <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th w={isReview ? "30%" : "35%"}>{t(`features.InvestmentScreening.Equipment.label.projectName`)}
                  <FilterTextBox
                    placeholder={t("common.partialMatch")}
                    filterValue={filterProjectName}
                    setFilterValue={setFilterProjectName}
                  />
                </Th>
                <Th w={isReview ? "30%" : "40%"}>PartNo
                  <FilterTextBox
                    placeholder={t("common.partialMatch")}
                    filterValue={filterPartNo}
                    setFilterValue={setFilterPartNo}
                  />
                </Th>
                <Th w="15%">{t(`common.field.approveDate`)}</Th>
                {isReview && <Th w="15%">{t(`${BP}.modal.reviewDate`)}</Th>}
                <Th w="10%"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((item: any) => (
                <Tr key={isReview ? `${item.projectId}_${item.reviewDate}` : item.projectId}>
                  <Td maxW="100%" isTruncated>
                    <TruncatedCell label={item.projectName}>
                      {item.projectName}
                    </TruncatedCell>
                  </Td>
                  <Td maxW="100%" isTruncated>
                    {(() => {
                      const label = (item.partNoDetails && item.partNoDetails.length > 0)
                        ? item.partNoDetails
                          .map((d: PartNoDetail) => d.displayPartNo
                            ? `${d.displayPartNo}（${t('features.InvestmentScreening.BasicInfo.label.tentativePartNoLabel', { partNo: d.partNo })}）`
                            : d.partNo)
                          .join(", ")
                        : item.partNo;
                      return (
                        <TruncatedCell label={label}>
                          {label}
                        </TruncatedCell>
                      );
                    })()}
                  </Td>
                  <Td maxW="100%" isTruncated>
                    <TruncatedCell label={item.approveDay}>{item.approveDay}</TruncatedCell>
                  </Td>
                  {isReview && (
                    <Td maxW="100%" isTruncated>
                      <TruncatedCell label={item.reviewDate}>{item.reviewDate}</TruncatedCell>
                    </Td>
                  )}
                  <Td>
                    <Button
                      size="sm"
                      onClick={() => isReview
                        ? onSelect(item.projectId, item.projectName, item.reviewDate)
                        : onSelect(item.projectId, item.projectName)
                      }
                      isLoading={isSearchLoading}
                    >
                      {t(`${BP}.modal.selectButton`)}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <Text pt={4}>{t(`${BP}.modal.noResults`)}</Text>
        )}
      </Box>
    );
  };

  const isExpanded = searchStep !== "project" || appResults.length > 0;

  return (
    <Draggable
      nodeRef={draggableRef}
      handle=".drag-handle"
      defaultPosition={panelDefaultPos.current}
      bounds={{
        top: 0,       // 上端（ヘッダー）は画面の一番上（0px）より上に行かないようにする
        left: -650,   // 横方向はパネル幅（700px）の大部分がはみ出してもOKにする
        right: window.innerWidth - 100, // 右側も100pxだけ画面内に残ればOK
        bottom: window.innerHeight - 50 // 下側もヘッダー分残ればOK
      }}
    >
      <Box
        ref={draggableRef}
        position="fixed"
        left={0}
        top={0}
        zIndex={1500}
        bg="white"
        borderRadius="md"
        boxShadow="2xl"
        border="1px solid"
        borderColor="gray.300"
        resize={isExpanded ? "both" : "none"}
        overflow="hidden"
        w="860px"
        h={isExpanded ? "600px" : "auto"}
        minW="600px"
        minH="150px"
        maxW="100vw"
        maxH="100vh"
        display="flex"
        flexDirection="column"
      >
        {/* ドラッグハンドル */}
        <Flex
          className="drag-handle"
          bg="gray.100"
          borderTopRadius="md"
          px={4}
          py={3}
          cursor="grab"
          justify="space-between"
          align="center"
          userSelect="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          flexShrink={0}
        >
          <HStack spacing={3}>
            {(searchStep === "item" || searchStep === "result") && (
              <Button
                size="sm"
                variant="ghost"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setSearchStep(searchStep === "result" ? "item" : "project")}
              >
                {t(`${BP}.modal.backButton`)}
              </Button>
            )}
            <Text fontWeight="bold">
              {searchStep === "project" ? t(`${BP}.modal.title`) : browsingProjectName}
            </Text>
          </HStack>
          <CloseButton
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClose}
          />
        </Flex>
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          mb={3}
        >
          {/* Step1: 検索入力 + 結果一覧 */}
          {searchStep === "project" ? (
            <Box display="flex" flexDirection="column" flex="1" minH="0" overflow="hidden">
              {/* 検索入力エリア（固定） */}
              <Box px={4} pt={4} pb={2} flexShrink={0} borderBottom="1px solid" borderColor="gray.100">
                <HStack spacing={2}>
                  <Input
                    placeholder={t(`${BP}.modal.searchPlaceholder`)}
                    value={searchPartNoInput}
                    onChange={(e) => setSearchPartNoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearchPartNo();
                    }}
                  />
                  <Button
                    colorScheme="blue"
                    onClick={handleSearchPartNo}
                    isLoading={isSearchLoading}
                    flexShrink={0}
                  >
                    {t(`${BP}.modal.searchButton`)}
                  </Button>
                </HStack>
                {appHasMore && (
                  <Text fontSize="sm" color="orange.600" mt={2}>
                    {t(`${BP}.modal.tooManyResults`)}
                  </Text>
                )}
              </Box>

              {/* 検索結果エリア（スクロール） */}
              {(appResults.length > 0 || reviewResults.length > 0) && (
                <Tabs index={tabIndex} onChange={setTabIndex} variant="enclosed" flex="1" minH="0" display="flex" flexDirection="column" mt={2}>
                  <TabList px={4} flexShrink={0}>
                    <Tab>{t(`${BP}.modal.tabApplication`)}</Tab>
                    <Tab>{t(`${BP}.modal.tabReview`)}</Tab>
                  </TabList>

                  <TabPanels flex="1" minH="0" overflow="hidden" px={4} pb={4}>
                    {/* 申請時タブ */}
                    <TabPanel p={0} pt={2} display="flex" flexDirection="column" h="100%" minH="0">
                      {appHasMore && (
                        <Text fontSize="sm" color="orange.600" mb={2} flexShrink={0}>
                          {t(`${BP}.modal.tooManyResults`)}
                        </Text>
                      )}
                      <ProjectListTable
                        data={appFilterData}
                        isReview={false}
                        isSearchLoading={isSearchLoading}
                        filterProjectName={filterProjectName}
                        setFilterProjectName={setFilterProjectName}
                        filterPartNo={filterPartNo}
                        setFilterPartNo={setFilterPartNo}
                        onSelect={handleSelectProject}
                      />
                    </TabPanel>

                    {/* 振り返りタブ */}
                    <TabPanel p={0} pt={2} display="flex" flexDirection="column" h="100%" minH="0">
                      {reviewHasMore && (
                        <Text fontSize="sm" color="orange.600" mb={2} flexShrink={0}>
                          {t(`${BP}.modal.tooManyResults`)}
                        </Text>
                      )}
                      <ProjectListTable
                        data={reviewFilterData}
                        isReview={true}
                        isSearchLoading={isSearchLoading}
                        filterProjectName={filterProjectName}
                        setFilterProjectName={setFilterProjectName}
                        filterPartNo={filterPartNo}
                        setFilterPartNo={setFilterPartNo}
                        onSelect={handleSelectProject}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}
            </Box>
          ) : searchStep === "item" && browsingBenchmarkData ? (
            /* Step2: ベンチマーク選択 */
            <Box overflowY="auto" p={4} flex="1">
              {isSearchLoading ? (
                <Flex justify="center"><Spinner /></Flex>
              ) : browsingBenchmarkData.benchmarks.length === 0 ? (
                <Text>{t(`${BP}.modal.noResults`)}</Text>
              ) : browsingBenchmarkData.benchmarks[0]?.inputType === "equipment" ? (
                <VStack align="stretch" spacing={2}>
                  {Array.from(
                    browsingBenchmarkData.benchmarks.reduce((acc, item) => {
                      const key = item.processCode ?? "";
                      if (!acc.has(key)) acc.set(key, []);
                      acc.get(key)!.push(item);
                      return acc;
                    }, new Map<string, BenchmarkItem[]>()).entries()
                  ).sort(([a], [b]) => b.localeCompare(a, "ja")).map(([processCode, items]) => (
                    <Box key={processCode} border="1px solid" borderColor="gray.200" p={3}>
                      <Text fontWeight="bold" mb={2}>
                        {t(`${BP}.modal.processLabel`)}: {processCode}
                      </Text>
                      <VStack align="stretch" spacing={1}>
                        {items.map((item) => (
                          <HStack key={item.equipmentId} justify="space-between" p={2} bg="gray.50">
                            <VStack align="flex-start" spacing={0}>
                              <Text fontSize="sm">
                                {t(`${BP}.modal.equipmentLabel`)}: <TruncatedCell label={item.equipmentName ?? ""}>{item.equipmentName}</TruncatedCell>
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {t(`${BP}.modal.monthLabel`)}: {item.benchmarkMonth}
                              </Text>
                            </VStack>
                            <Button size="sm" onClick={() => handleSelectBenchmark(item)}>
                              {t(`${BP}.modal.selectButton`)}
                            </Button>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {browsingBenchmarkData.benchmarks.map((item) => (
                    <HStack
                      key={item.productionLineId}
                      justify="space-between"
                      p={3}
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <VStack align="flex-start" spacing={0}>
                        <Text fontWeight="bold">
                          {t(`${BP}.modal.lineLabel`)}:{" "}
                          {item.productionLineName?.trim() || t(`${BP}.modal.noLineName`)}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {t(`${BP}.modal.monthLabel`)}: {item.benchmarkMonth}
                        </Text>
                      </VStack>
                      <Button size="sm" onClick={() => handleSelectBenchmark(item)}>
                        {t(`${BP}.modal.selectButton`)}
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>
          ) : searchStep === "result" && selectedPastBenchmark ? (
            /* Step3: 選択結果の比較表示 */
            <Box overflowY="auto" p={4} flex="1">
              <VStack align="stretch" spacing={4}>
                {/* 選択情報サマリー */}
                <Text fontSize="sm" color="gray.600">
                  {selectedPastBenchmark.inputType === "equipment"
                    ? `${t(`${BP}.pastBenchmarkSection.equipment`)}: ${selectedPastBenchmark.equipmentName}`
                    : `${t(`${BP}.pastBenchmarkSection.line`)}: ${selectedPastBenchmark.productionLineName?.trim() || t(`${BP}.modal.noLineName`)}`}
                  {" | "}
                  {t(`${BP}.pastBenchmarkSection.month`)}: {selectedPastBenchmark.benchmarkMonth}
                </Text>

                {/* PartNoドロップダウン */}
                {selectedPastBenchmark.benchmarkByPartNo &&
                  Object.keys(selectedPastBenchmark.benchmarkByPartNo).length > 0 && (
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        {t(`${BP}.pastBenchmarkSection.partNo`)}:
                      </Text>
                      <Select
                        size="sm"
                        w="200px"
                        value={comparePartNo || ""}
                        onChange={(e) => setComparePartNo(e.target.value || null)}
                      >
                        {Object.keys(selectedPastBenchmark.benchmarkByPartNo).map((partNo) => (
                          <option key={partNo} value={partNo}>
                            {resolveDisplayPartNoByKey(partNo, selectedProjectPartNoDetails)}
                          </option>
                        ))}
                      </Select>
                    </HStack>
                  )}

                {/* ベンチマーク値テーブル */}
                {compareValues && (
                  <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200">
                    <Thead bg="gray.100" top={0} zIndex={1}>
                      <Tr>
                        <Th w="160px">{t("common.label.item")}</Th>
                        <Th textAlign="center">{t(`${BP}.label.actualBest`)}</Th>
                        <Th textAlign="center">{t(`${BP}.label.theoretical`)}</Th>
                        <Th textAlign="center">{t(`${BP}.label.investmentPlan`)}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td fontWeight="semibold">{t(`${BP}.label.yield`)}</Td>
                        <Td textAlign="right">{compareValues.actualBest?.yield != null ? `${Number(compareValues.actualBest.yield).toFixed(2)}%` : "-"}</Td>
                        <Td textAlign="right">{compareValues.theoretical?.yield != null ? `${Number(compareValues.theoretical.yield).toFixed(2)}%` : "-"}</Td>
                        <Td textAlign="right">{compareValues.investmentPlan?.yield != null ? `${Number(compareValues.investmentPlan.yield).toFixed(2)}%` : "-"}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold">{t(`${BP}.label.operatingRate`)}</Td>
                        <Td textAlign="right">{compareValues.actualBest?.operatingRate != null ? `${Number(compareValues.actualBest.operatingRate).toFixed(1)}%` : "-"}</Td>
                        <Td textAlign="right">{compareValues.theoretical?.operatingRate != null ? `${Number(compareValues.theoretical.operatingRate).toFixed(1)}%` : "-"}</Td>
                        <Td textAlign="right">{compareValues.investmentPlan?.operatingRate != null ? `${Number(compareValues.investmentPlan.operatingRate).toFixed(1)}%` : "-"}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold">{t(`${BP}.label.cycleTime`)}</Td>
                        <Td textAlign="right">{compareValues.actualBest?.cycleTime != null ? `${Number(compareValues.actualBest.cycleTime).toFixed(1)}${t(`${BP}.placeholder.seconds`)}` : "-"}</Td>
                        <Td textAlign="right">{compareValues.theoretical?.cycleTime != null ? `${Number(compareValues.theoretical.cycleTime).toFixed(1)}${t(`${BP}.placeholder.seconds`)}` : "-"}</Td>
                        <Td textAlign="right">{compareValues.investmentPlan?.cycleTime != null ? `${Number(compareValues.investmentPlan.cycleTime).toFixed(1)}${t(`${BP}.placeholder.seconds`)}` : "-"}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold">{t(`${BP}.label.personnelPerDay`)}</Td>
                        <Td textAlign="right">{compareValues.actualBest?.personnelPerDay != null ? `${compareValues.actualBest.personnelPerDay}` : "-"}</Td>
                        <Td textAlign="right">{compareValues.theoretical?.personnelPerDay != null ? `${compareValues.theoretical.personnelPerDay}` : "-"}</Td>
                        <Td textAlign="right">{compareValues.investmentPlan?.personnelPerDay != null ? `${compareValues.investmentPlan.personnelPerDay}` : "-"}</Td>
                      </Tr>
                      <Tr bg="gray.50">
                        <Td fontWeight="semibold">{t(`${BP}.label.productionVolume`)}</Td>
                        <Td textAlign="right">{compareValues.actualBest?.productionVolume != null ? Number(compareValues.actualBest.productionVolume).toLocaleString() : "-"}</Td>
                        <Td textAlign="right">{compareValues.theoretical?.productionVolume != null ? Number(compareValues.theoretical.productionVolume).toLocaleString() : "-"}</Td>
                        <Td textAlign="right">{compareValues.investmentPlan?.productionVolume != null ? Number(compareValues.investmentPlan.productionVolume).toLocaleString() : "-"}</Td>
                      </Tr>
                      <Tr bg="gray.50">
                        <Td fontWeight="semibold">{t(`${BP}.label.productionPerEquipment`)}</Td>
                        <Td textAlign="right">{compareValues.actualBest?.productionPerEquipment != null ? Number(compareValues.actualBest.productionPerEquipment).toFixed(2) : "-"}</Td>
                        <Td textAlign="right">{compareValues.theoretical?.productionPerEquipment != null ? Number(compareValues.theoretical.productionPerEquipment).toFixed(2) : "-"}</Td>
                        <Td textAlign="right">{compareValues.investmentPlan?.productionPerEquipment != null ? Number(compareValues.investmentPlan.productionPerEquipment).toFixed(2) : "-"}</Td>
                      </Tr>
                      <Tr bg="gray.50">
                        <Td fontWeight="semibold">{t(`${BP}.label.productionPerPerson`)}</Td>
                        <Td textAlign="right">{compareValues.actualBest?.productionPerPerson != null ? Number(compareValues.actualBest.productionPerPerson).toFixed(2) : "-"}</Td>
                        <Td textAlign="right">{compareValues.theoretical?.productionPerPerson != null ? Number(compareValues.theoretical.productionPerPerson).toFixed(2) : "-"}</Td>
                        <Td textAlign="right">{compareValues.investmentPlan?.productionPerPerson != null ? Number(compareValues.investmentPlan.productionPerPerson).toFixed(2) : "-"}</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                )}

                {/* 分析・対策テキストセクション */}
                {(selectedPastBenchmark.gapAnalysis ||
                  selectedPastBenchmark.productionPerEquipment ||
                  selectedPastBenchmark.productionPerPerson ||
                  selectedPastBenchmark.nonOperationMeasures ||
                  selectedPastBenchmark.yieldMeasures) && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>
                        {t(`${BP}.label.analysisSection`)}
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        {selectedPastBenchmark.gapAnalysis && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600">{t(`${BP}.label.gapAnalysis`)}</Text>
                            <Text fontSize="sm" whiteSpace="pre-wrap" bg="gray.50" p={2} borderRadius="md">{selectedPastBenchmark.gapAnalysis}</Text>
                          </Box>
                        )}
                        {selectedPastBenchmark.productionPerEquipment && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600">{t(`${BP}.label.productionPerEquipmentMeasures`)}</Text>
                            <Text fontSize="sm" whiteSpace="pre-wrap" bg="gray.50" p={2} borderRadius="md">{selectedPastBenchmark.productionPerEquipment}</Text>
                          </Box>
                        )}
                        {selectedPastBenchmark.productionPerPerson && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600">{t(`${BP}.label.productionPerPersonMeasures`)}</Text>
                            <Text fontSize="sm" whiteSpace="pre-wrap" bg="gray.50" p={2} borderRadius="md">{selectedPastBenchmark.productionPerPerson}</Text>
                          </Box>
                        )}
                        {selectedPastBenchmark.nonOperationMeasures && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600">{t(`${BP}.label.nonOperationMeasures`)}</Text>
                            <Text fontSize="sm" whiteSpace="pre-wrap" bg="gray.50" p={2} borderRadius="md">{selectedPastBenchmark.nonOperationMeasures}</Text>
                          </Box>
                        )}
                        {selectedPastBenchmark.yieldMeasures && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600">{t(`${BP}.label.yieldMeasures`)}</Text>
                            <Text fontSize="sm" whiteSpace="pre-wrap" bg="gray.50" p={2} borderRadius="md">{selectedPastBenchmark.yieldMeasures}</Text>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  )}
              </VStack>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Draggable>
  );
};

export default PastBenchmarkSearchPanel;
